import EditSaveButtons from './EditSaveButtons';

/**
 * DetailsTabLayout - Reusable layout component for Details tabs across all admin sections
 *
 * Features:
 * - Full-width fields (each field takes a whole row)
 * - Beautiful gradient dividers between sections
 * - Consistent spacing and styling
 * - Edit/Save/Cancel button management
 *
 * @param {Object} props
 * @param {string} props.title - Title for the details section
 * @param {string} props.subtitle - Subtitle/description text
 * @param {boolean} props.isEditMode - Whether currently in edit mode
 * @param {boolean} props.isSaving - Whether save operation is in progress
 * @param {Function} props.onEdit - Callback when Edit button is clicked
 * @param {Function} props.onSave - Callback when Save button is clicked
 * @param {Function} props.onCancel - Callback when Cancel button is clicked
 * @param {ReactNode} props.children - Content to render (fields with dividers)
 * @param {boolean} props.showEditButtons - Whether to show edit/save/cancel buttons (default: true)
 */
export default function DetailsTabLayout({
  title = 'Details',
  subtitle = 'Manage parameters and settings',
  isEditMode = false,
  isSaving = false,
  onEdit,
  onSave,
  onCancel,
  children,
  showEditButtons = true
}) {

  return (
    <div className="p-8 max-w-5xl">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">{title}</h2>
          <p className="text-sm text-gray-400">{subtitle}</p>
        </div>

        {/* Edit/Save/Cancel Buttons */}
        {showEditButtons && (
          <EditSaveButtons
            isEditMode={isEditMode}
            isSaving={isSaving}
            onEdit={onEdit}
            onSave={onSave}
            onCancel={onCancel}
          />
        )}
      </div>

      {/* Content with consistent spacing */}
      <div className="space-y-8">
        {children}
      </div>
    </div>
  );
}

/**
 * FieldSection - Individual field wrapper for use within DetailsTabLayout
 *
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.helperText - Optional helper text shown on the right
 * @param {ReactNode} props.children - Field content (input, dropdown, etc.)
 */
export function FieldSection({ label, helperText, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <label className="block text-sm font-semibold text-gray-200">
          {label}
        </label>
        {helperText && (
          <span className="text-xs text-gray-500">{helperText}</span>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * SectionDivider - Beautiful gradient divider between field sections
 */
export function SectionDivider() {
  return (
    <div className="h-px bg-gradient-to-r from-transparent via-gray-700/50 to-transparent"></div>
  );
}
